import { Graphics } from '@pixi/react';
import { Graphics as PixiGraphics } from 'pixi.js';
import { useCallback, useMemo } from 'react';
import { DungeonEntity } from '../../entities/DungeonEntity';
import { tileWidth, tileHeight } from '../../data/tileset.config';
import { getTargetPosition } from '../../utils/map.utils';
import { mapService } from '../../services/MapService';
import { useGameStore } from '../../store/gameSlice';

interface AimingOverlayProps {
  player?: DungeonEntity;
  isVisible: boolean;
  maxDistance?: number;
}

export const AimingOverlay = ({
  player,
  isVisible,
  maxDistance = 10,
}: AimingOverlayProps) => {
  const zoomFactor = useGameStore((s) => s.zoomFactor);

  const targetPositions = useMemo(() => {
    if (!isVisible || !player) {
      return [];
    }

    const positions: { x: number; y: number }[] = [];
    let currentX = player.positionX;
    let currentY = player.positionY;

    for (let i = 0; i < maxDistance; i++) {
      const target = getTargetPosition(currentX, currentY, player.direction);

      const tile = mapService.getTileAt(target.x, target.y);
      if (!tile || tile.type !== 'ground') {
        break;
      }

      positions.push(target);

      currentX = target.x;
      currentY = target.y;
    }

    return positions;
  }, [isVisible, player, maxDistance]);

  const draw = useCallback(
    (g: PixiGraphics) => {
      g.clear();

      if (!isVisible || targetPositions.length === 0) {
        return;
      }

      targetPositions.forEach((pos) => {
        const worldX = pos.x * tileWidth * zoomFactor;
        const worldY = -pos.y * tileHeight * zoomFactor;
        const sizeW = tileWidth;
        const sizeH = tileHeight;

        g.lineStyle(2, 0xff0000, 0.6);
        g.drawRect(worldX - sizeW / 2, worldY - sizeH / 2, sizeW, sizeH);
      });
    },
    [isVisible, targetPositions, zoomFactor],
  );

  if (!isVisible) {
    return null;
  }

  return <Graphics draw={draw} zIndex={100} />;
};
